'use client';

import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["small", "size", "indeterminate", "id", "children", "semanticColor", "isHover", "isFocus", "isActive", "as"];
import React, { useContext } from 'react';
import { BrowserDefaultFocusStyleContext } from "../../contexts/BrowserDefaultFocusStyleContext.mjs";
import { ENCORE_DATA_IDS } from "../../dataEncoreIds.mjs";
import { camelToKebab, textClass } from "../../styles/index.mjs";
import { VisuallyHidden } from "../VisuallyHidden/index.mjs";
import { useDefaultProps } from "../../hooks/useDefaultProps.mjs";
import { ecx } from "../../utils.mjs";
/**
 * FormCheckbox allow users to select one or more items from a set, turn an option on or off, or acknowledge something. Used within a FormGroup component.
 *
 * [Storybook](https://encore-web.spotify.net/?path=/docs/components-formcheckbox--docs) |
 * [Encore site](https://encore.spotify.net/web/components/selectors#checkbox) |
 * [Github](https://ghe.spotify.net/encore/web/tree/master/src/core/components/FormCheckbox) |
 * [Figma](https://www.figma.com/design/bnQXqGkLeiTlbk8JXGDcsX/%E2%9A%99%EF%B8%8F-%E2%80%8A%E2%80%8BEncore-Web-Components?m=auto&node-id=32403-12297&t=uvnU5TzlyKmXuODT-1)
 */
export var FormCheckbox = /*#__PURE__*/React.forwardRef(function FormCheckbox(inProps, ref) {
  var propsWithProviderDefaults = useDefaultProps({
    props: inProps,
    name: 'FormCheckbox'
  });
  var small = propsWithProviderDefaults.small,
    _propsWithProviderDef = propsWithProviderDefaults.size,
    size = _propsWithProviderDef === void 0 ? 'medium' : _propsWithProviderDef,
    indeterminate = propsWithProviderDefaults.indeterminate,
    id = propsWithProviderDefaults.id,
    children = propsWithProviderDefaults.children,
    _propsWithProviderDef2 = propsWithProviderDefaults.semanticColor,
    semanticColor = _propsWithProviderDef2 === void 0 ? 'essentialBrightAccent' : _propsWithProviderDef2,
    isHover = propsWithProviderDefaults.isHover,
    isFocus = propsWithProviderDefaults.isFocus,
    isActive = propsWithProviderDefaults.isActive,
    as = propsWithProviderDefaults.as,
    props = _objectWithoutProperties(propsWithProviderDefaults, _excluded);
  var _useContext = useContext(BrowserDefaultFocusStyleContext),
    useBrowserDefaultFocusStyle = _useContext.useBrowserDefaultFocusStyle;
  var Element = as || 'div';
  return /*#__PURE__*/React.createElement(Element, {
    className: ecx(['baseline', 'form-check']),
    "data-encore-id": ENCORE_DATA_IDS.FormCheckbox
  }, /*#__PURE__*/React.createElement(VisuallyHidden, Object.assign({
    component: "input",
    type: "checkbox",
    ref: ref,
    id: id
  }, props)), /*#__PURE__*/React.createElement("label", {
    htmlFor: id,
    className: ecx(['form-checkbox__label'])
  }, /*#__PURE__*/React.createElement("span", {
    className: ecx([{
      'form-checkbox__indicator--active': isActive,
      'form-checkbox__indicator--hover': isHover,
      'focus-border': isFocus || !useBrowserDefaultFocusStyle,
      'focus-border--focus': isFocus
    }, 'form-checkbox__indicator', 'form-check-indicator', indeterminate ? 'Encore_indeterminate' : '']),
    style: {
      '--encore-form-checkbox-semantic-color': "var(--".concat(camelToKebab(semanticColor), ")")
    }
  }), children && /*#__PURE__*/React.createElement("span", {
    className: ecx([{
      'form-checkbox__text--small': small || size === 'small'
    }, 'form-checkbox__text', textClass(small || size === 'small' ? 'marginal' : 'bodySmall')])
  }, children)));
});